import React, { Component } from 'react';
import { OnlineGameCard } from './OnlineGameCard';
import { OnlinePlayerControls } from './OnlinePlayerControls';
import { OnlineOtherSidePlayerView } from './OnlineOtherSidePlayerView';
import './OnlineGameBoard.css';
import { ActionType, CellType, GameSessionState } from './OnlineEnums';
import { ConnectedUserList } from './ConnectedUserList';

export class OnlineGameBoard extends Component {
    static displayName = OnlineGameBoard.name;

    intervalID;

  constructor(props) {
      super(props);

      this.guessWord = this.guessWord.bind(this);
      this.confirmGuess = this.confirmGuess.bind(this);
      this.concedeTurn = this.concedeTurn.bind(this);
      this.giveHint = this.giveHint.bind(this);
      this.isCardClickable = this.isCardClickable.bind(this);
      this.getRejoinLink = this.getRejoinLink.bind(this);

      this.state = {
          connectedPlayers: [],
          ourHints: [],
          theirHints: [],
          ourCellTypes: [],
          wordStates: [],
          gameFinishStatus: 0,
          waitingForGameActionType: -99,
          isWaitingForActionFromUs: false,
          unrevealedGreensLeft: 15,
          countersLeft: 9,
          guessedIndex: -1,
          prevSessionState: -1,
          loading: true, selectedCard: null, waitingForStateUpdate: true,
          isGameMaster: false,
          gameSessionId: ""
      };
  }

    componentDidMount() {
        this.updateGameStateSync();
    }

    componentWillUnmount() {
        this.stopServerPolling();
    }

    stopServerPolling() {
        clearTimeout(this.intervalID);
    }

    async updateGameStateAsync() {
        const response = await fetch('game/state');
        const data = await response.json();
        if (!response.ok) {
            alert("Server error: " + response.statusText);
            return;
        }

        this.setStateFromData(data);
        this.intervalID = setTimeout(this.updateGameState, 2000);
    }

    updateGameStateSync = () => {
        fetch('game/state')
            .then(r => r.json())
            .then(data => {
                if (this.setStateFromData(data))
                    this.intervalID = setTimeout(this.updateGameStateSync.bind(this), 2000);
                else
                    this.stopServerPolling();
            });
    }

    setStateFromData(data) {
        if (data.sessionStatus === GameSessionState.Abandoned) {
            alert("Game abandoned by host");
            return false;
        }

        this.setState({
            connectedPlayers: data.connectedPlayers
        });

        if (data.sessionStatus === GameSessionState.Lobby) {
            this.stopServerPolling();

            if (data.isGameMaster) {
                window.location = '/new-game-online/keepSession';
                return false;
            }

            // Game has ended and need to move back to lobby
            alert("Moving to new game");
            window.location = '/join-game-online/' + data.gameSessionId;
            return false;
        }

        if (this.state.waitingForGameActionType === ActionType.Finished) {
            // No point in making updates at this point
            return true;
        }

        if (this.state.waitingForGameActionType === data.gameState?.waitingForGameActionType) {
            // Update only when there's state change
            return true;
        }
        
        this.setState({
            loading: false,
            ourHints: data.gameState?.ourHints,
            theirHints: data.gameState?.othersHints,
            ourCellTypes: data.gameState?.ourCellTypes,
            wordStates: data.gameState?.wordStates,
            gameFinishStatus: data.gameState?.gameFinishStatus,
            waitingForGameActionType: data.gameState?.waitingForGameActionType,
            isWaitingForActionFromUs: data.gameState?.isWaitingForActionFromUs,
            unrevealedGreensLeft: data.gameState?.unrevealedGreensLeft,
            countersLeft: data.gameState?.countersLeft,
            guessedIndex: data.gameState?.guessedIndex,
            waitingForStateUpdate: false,
            prevSessionState: data.sessionStatus,
            isGameMaster: data.isGameMaster,
            gameSessionId: data.gameSessionId
        });

        return true;
    }

    isCardClickable() {
        return this.state.waitingForGameActionType === ActionType.Guess && this.state.isWaitingForActionFromUs && !this.state.waitingForStateUpdate;
    }

    getRejoinLink() {
        return 'http://' + window.location.host + '/rejoin-game-online/' + this.state.gameSessionId;
    }

    async giveHint(word, referencedCount) {
        if (word === "") {
            alert("Write the hint word");
            return;
        }
        if (referencedCount < 1 || referencedCount > 9) {
            alert("Must refer to 1 to 9 words on the grid");
            return;
        }

        const response = await fetch('game/action/hint', {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                hintWord: word,
                referencedWordCount: parseInt(referencedCount)
            })
        });
        const data = await response.json();
        if (!response.ok) {
            alert(this.getErrorMessage(response.status, response.statusText, data.errorMessage));
            return;
        }
        this.setState({
            waitingForStateUpdate: true
        });
    }

    getErrorMessage(statusCode, statusText, errorMessage) {
        if (errorMessage === "" || statusCode !== 403)
            return "Server error: " + statusText;

        switch (errorMessage) {
            case "HintMustBeSingleWord": return "Hint must be a single word";
            case "CannotHintWithWordThatsInTheGrid": return "The hinted word cannot be something that already appears on the grid";
            default: return errorMessage;
        }
    }

    async initiateRematch() {
        const response = await fetch('game/rematch', {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            }
        });
        await response.json();
        if (!response.ok) {
            alert("Server error: " + response.statusText);
            return;
        }
        this.setState({
            waitingForStateUpdate: true
        });
    }

    async guessWord(index) {
        if (this.state.waitingForStateUpdate)
            return;

        const response = await fetch('game/action/guess', {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                wordIndex: index
            })
        });
        await response.json();
        if (!response.ok) {
            alert("Server error: " + response.statusText);
            return;
        }
    }

    async confirmGuess() {
        if (this.state.waitingForStateUpdate)
            return;

        const response = await fetch('game/action/guess/confirm', {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            }
        });
        await response.json();
        if (!response.ok) {
            alert("Server error: " + response.statusText);
            return;
        }
    }

    async concedeTurn() {
        if (this.state.waitingForStateUpdate)
            return;

        const response = await fetch('game/action/guess/quit', {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            }
        });
        await response.json();
        if (!response.ok) {
            alert("Server error: " + response.statusText);
            return;
        }
    }

    getVisibleCellUsOverride(index) {
        if (!this.state.isWaitingForActionFromUs)
            return null;

        switch (this.state.waitingForGameActionType) {
            case ActionType.GiveHint:
            case ActionType.FirstHint:
                // Display the corresponding colors to aim with coming up with hint in our side
                switch (this.state.ourCellTypes[index]) {
                    case CellType.Black: return CellType.Black;
                    case CellType.Green: return CellType.Green;
                    default: return null;
                }
            default: return null;
        }
    }

    getDimGuessedCard() {
        if (!this.state.isWaitingForActionFromUs)
            return false;

        switch (this.state.waitingForGameActionType) {
            case ActionType.GiveHint:
            case ActionType.FirstHint:
                return true;
            default: return false;
        }
    }

  renderwordsTable(words) {
      return (
          <div className="fullgame">
              <OnlineOtherSidePlayerView
                  nextAction={this.state.waitingForGameActionType}
                  isWaitingForAction={!this.state.isWaitingForActionFromUs}
                  givenHints={this.state.theirHints} />

              <div className="gameboard">
                  {this.state.wordStates.map(w =>
                      <OnlineGameCard key={'word' + w.index} word={w.word} index={w.index} visibleUs={w.ourVisibleCellType} visibleThem={w.theirVisibleCellType}
                          isClickable={this.isCardClickable()} showGuessConfirmation={this.state.guessedIndex === w.index} isWaitingForActionFromUs={this.state.isWaitingForActionFromUs}
                          confirmGuessFunc={this.confirmGuess} makeGuessFunc={this.guessWord} visibleCellUsOverride={this.getVisibleCellUsOverride(w.index)}
                          dimGuessedCards={this.getDimGuessedCard()} />
                  )}
              </div>

              <OnlinePlayerControls
                  nextAction={this.state.waitingForGameActionType}
                  isWaitingForAction={this.state.isWaitingForActionFromUs}
                  givenHints={this.state.ourHints}
                  giveHintFunc={this.giveHint}
                  gameFinishStatus={this.state.gameFinishStatus}
                  countersLeft={this.state.countersLeft}
                  unrevealedGreensLeft={this.state.unrevealedGreensLeft}
                  confirmGuessFunc={this.confirmGuess}
                  ourCellTypes={this.state.ourCellTypes}
                  rematchFunc={this.initiateRematch}
                  isGameMaster={this.state.isGameMaster}
                  rejoinLinkGetterFunc={this.getRejoinLink}
                  connectedPlayers={this.state.connectedPlayers}
                  concedeTurnAction={this.concedeTurn} />

              <ConnectedUserList users={this.state.connectedPlayers} />
          </div>
    );
  }

  render() {
      let contents = this.state.loading || this.state.wordStates == null
      ? <p><em>Loading... (if this happens during ongoing game then ask for rejoin link from the other player still in the game)</em></p>
      : this.renderwordsTable(this.state.words);

    return (
      <div>
        {contents}
      </div>
    );
  }
}
